/**
 * Lingering on a lesson (community-feed-invite DESIGN.md §4.3): a stream
 * LessonCard at least 60% in view for 3 seconds in a row, while the page is
 * visible and the seeker touched, scrolled, clicked or typed in the last 30
 * seconds. The activity guard stops a parked screen from "reading" every card
 * in view.
 *
 * One IntersectionObserver (root: the scroll container, thresholds [0, 0.6])
 * records when each card came into view; a 500ms ticker credits it once.
 * Leaving 60%, or the page hiding, resets that card's clock. Without an
 * IntersectionObserver nothing lingers (opens still count).
 *
 *   const linger = useLessonLinger({ root: () => paneEl, onRead: (id) => … , onActive })
 *   linger.observe(el)   // el carries data-lesson-id
 */
import { onBeforeUnmount } from 'vue'
import { RULES } from '@/utils/lesson-invite'

const TICK_MS = 500
const ACTIVITY = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'click']

export function useLessonLinger({ root = () => null, onRead = () => {}, onActive = () => {} } = {}) {
  const hasIO = typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function'
  const inView = new Set()          // ids at 60% or more
  const since = new Map()           // id → when its current stretch in view began
  const credited = new Set()
  const watched = new WeakSet()
  let lastActive = 0
  let io = null
  let ticker = null

  const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden'
  const idOf = (el) => (el && el.dataset ? el.dataset.lessonId : '') || ''

  function ensure() {
    if (io || !hasIO) return io
    try {
      io = new window.IntersectionObserver((entries) => {
        const t = Date.now()
        entries.forEach((e) => {
          const id = idOf(e.target)
          if (!id) return
          if (e.intersectionRatio >= RULES.LINGER_RATIO) {
            inView.add(id)
            if (!since.has(id) && visible()) since.set(id, t)
          } else {
            inView.delete(id)
            since.delete(id)
          }
        })
      }, { root: root() || null, threshold: [0, RULES.LINGER_RATIO] })
    } catch (e) { io = null }
    if (io && !ticker) ticker = setInterval(tick, TICK_MS)
    return io
  }
  function tick() {
    if (!visible()) { since.clear(); return }
    const t = Date.now()
    if (t - lastActive > RULES.ACTIVE_MS) return
    since.forEach((from, id) => {
      if (t - from >= RULES.LINGER_MS) {
        since.delete(id)
        if (credited.has(id)) return
        credited.add(id)
        try { onRead(id) } catch (e) { /* never stop the ticker */ }
      }
    })
  }
  function observe(el) {
    if (!el || watched.has(el) || !ensure()) return
    watched.add(el)
    io.observe(el)
  }
  function active() { lastActive = Date.now(); try { onActive() } catch (e) { /* */ } }
  // Back on the page: the cards still in view start a fresh clock.
  function onVisibility() {
    since.clear()
    if (visible()) { const t = Date.now(); inView.forEach((id) => { if (!credited.has(id)) since.set(id, t) }) }
  }

  if (typeof document !== 'undefined') {
    ACTIVITY.forEach((ev) => document.addEventListener(ev, active, { passive: true, capture: true }))
    // A scroll doesn't bubble: heard in the capture phase, from the pane or the page.
    document.addEventListener('scroll', active, { passive: true, capture: true })
    document.addEventListener('visibilitychange', onVisibility)
  }
  function stop() {
    if (ticker) clearInterval(ticker)
    ticker = null
    if (io) { try { io.disconnect() } catch (e) { /* */ } }
    io = null
    if (typeof document !== 'undefined') {
      ACTIVITY.forEach((ev) => document.removeEventListener(ev, active, { capture: true }))
      document.removeEventListener('scroll', active, { capture: true })
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }
  onBeforeUnmount(stop)

  return { observe, stop, markActive: active }
}
